import React, { Fragment, useState } from 'react';
import { bool, func } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { injectIntl, intlShape, FormattedMessage } from 'react-intl';
import { pathOr } from 'ramda';

import { ensureCurrentUser } from '../../../../util/data';
import { propTypes } from '../../../../util/types';
import { isScrollingDisabled, manageDisableScrolling } from '../../../../ducks/UI.duck';
import { stripeAccountClearError } from '../../../../ducks/stripeConnectAccount.duck';
import { PayoutDetailsForm } from '../../../../forms';

import { savePayoutDetails } from './PayoutDetailsTab.duck';
import css from './PayoutDetailsTab.module.css';
import { updateProfile } from '../../../../ducks/userSettings.duck';

const PayoutDetailsTab = (props) => {
  const {
    currentUser,
    createStripeAccountError,
    onPayoutDetailsFormChange,
    onPayoutDetailsFormSubmit,
    onUpdateProfile,
    onManageDisableScrolling,
    payoutDetailsSaveInProgress,
    payoutDetailsSaved,
    showBirthdayDateField,
    showBirthPlaceField,
    intl,
  } = props;

  const ensuredCurrentUser = ensureCurrentUser(currentUser);
  const currentUserLoaded = !!ensuredCurrentUser.id;
  const { stripeConnected } = ensuredCurrentUser.attributes;
  const [cniFormData, setCniFormData] = useState(null);
  const [proofOfAddressFormData, setProofOfAddressFormData] = useState(null);
  const taxIdNumber = pathOr(
    '',
    ['attributes', 'profile', 'privateData', 'taxIdNumber'],
    ensuredCurrentUser
  );
  const hasNoTaxIdNumber = pathOr(
    false,
    ['attributes', 'profile', 'privateData', 'hasNoTaxIdNumber'],
    ensuredCurrentUser
  );
  const formDisabled = !currentUserLoaded || stripeConnected || payoutDetailsSaved;
  let message = <FormattedMessage id="PayoutDetailsTab.loadingData" />;

  if (currentUserLoaded && payoutDetailsSaved) {
    message = <FormattedMessage id="PayoutDetailsTab.payoutDetailsSaved" />;
  } else if (currentUserLoaded && stripeConnected) {
    message = <FormattedMessage id="PayoutDetailsTab.stripeAlreadyConnected" />;
  } else if (currentUserLoaded && !stripeConnected) {
    message = <FormattedMessage id="PayoutDetailsTab.stripeNotConnected" />;
  }
  const setCNIDocument = (cniFormDataValue) => {
    setCniFormData(cniFormDataValue);
  };
  const setProofOfAddressDocument = (proofOfAddressFormDataValue) => {
    setProofOfAddressFormData(proofOfAddressFormDataValue);
  };
  const handleSaveTaxIdNumber = ({ taxIdNumber, hasNoTaxIdNumber }) => {
    onUpdateProfile({
      privateData: {
        taxIdNumber: hasNoTaxIdNumber ? null : taxIdNumber || null,
        hasNoTaxIdNumber: !!hasNoTaxIdNumber,
      },
    });
  };
  const handlePayoutDetailsSubmit = (values) => {
    const {
      fname: firstName,
      lname: lastName,
      taxIdNumber,
      hasNoTaxIdNumber,
      birthdayDate,
      ...rest
    } = values;

    onPayoutDetailsFormSubmit({
      firstName,
      lastName,
      birthdayDate,
      cniFormData,
      proofOfAddressFormData,
      ...rest,
    });
    handleSaveTaxIdNumber({ taxIdNumber, hasNoTaxIdNumber });
  };
  const showForm =
    currentUserLoaded && (payoutDetailsSaveInProgress || payoutDetailsSaved || !stripeConnected);

  return (
    <Fragment>
      <p className={css.message}>{message}</p>
      {showForm ? (
        <PayoutDetailsForm
          disabled={formDisabled}
          inProgress={payoutDetailsSaveInProgress}
          ready={payoutDetailsSaved}
          submitButtonText={intl.formatMessage({ id: 'PayoutDetailsTab.submitButtonText' })}
          createStripeAccountError={createStripeAccountError}
          onChange={onPayoutDetailsFormChange}
          onSubmit={handlePayoutDetailsSubmit}
          initialValues={{ taxIdNumber, hasNoTaxIdNumber }}
          setCNIDocument={setCNIDocument}
          setProofOfAddressDocument={setProofOfAddressDocument}
          showBirthdayDateField={showBirthdayDateField}
          showBirthPlaceField={showBirthPlaceField}
          onManageDisableScrolling={onManageDisableScrolling}
        />
      ) : null}
    </Fragment>
  );
};

PayoutDetailsTab.defaultProps = {
  currentUser: null,
  createStripeAccountError: null,
};

PayoutDetailsTab.propTypes = {
  currentUser: propTypes.currentUser,
  scrollingDisabled: bool.isRequired,
  payoutDetailsSaveInProgress: bool.isRequired,
  createStripeAccountError: propTypes.error,
  payoutDetailsSaved: bool.isRequired,

  onPayoutDetailsFormChange: func.isRequired,
  onPayoutDetailsFormSubmit: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  showBirthdayDateField: bool.isRequired,
  showBirthPlaceField: bool.isRequired,
  currentUserHasListings: bool.isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = (state) => {
  const { createStripeAccountError, currentUser, currentUserHasListings } = state.user;
  const { payoutDetailsSaveInProgress, payoutDetailsSaved } = state.PayoutDetailsTab;
  const birthdayDate = pathOr(
    false,
    ['currentUser', 'attributes', 'profile', 'protectedData', 'birthdayDate'],
    state.user
  );
  const birthPlace = pathOr(
    false,
    ['currentUser', 'attributes', 'profile', 'protectedData', 'birthPlace'],
    state.user
  );
  return {
    currentUser,
    createStripeAccountError,
    payoutDetailsSaveInProgress,
    payoutDetailsSaved,
    scrollingDisabled: isScrollingDisabled(state),
    showBirthdayDateField: !birthdayDate,
    showBirthPlaceField: !birthPlace,
    currentUserHasListings,
  };
};

const mapDispatchToProps = (dispatch) => ({
  onPayoutDetailsFormChange: () => dispatch(stripeAccountClearError()),
  onPayoutDetailsFormSubmit: (values) => dispatch(savePayoutDetails(values)),
  onUpdateProfile: (data) => dispatch(updateProfile(data)),
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

export default compose(connect(mapStateToProps, mapDispatchToProps), injectIntl)(PayoutDetailsTab);
